import React, { useState, useRef } from 'react';
import { SetupState, ThemePreset, FeeType } from '../types';
import { themePresets } from '../data/themes';
import { 
  Palette, 
  Upload, 
  Trash2, 
  ZoomIn, 
  ZoomOut, 
  RotateCcw, 
  Zap, 
  Droplet, 
  Settings, 
  CreditCard, 
  User, 
  Percent, 
  ChevronDown, 
  ChevronUp, 
  Info,
  CheckCircle2,
  Lock,
  Plus,
  Minus
} from 'lucide-react';

interface Step2FormProps {
  state: SetupState;
  onChange: (updates: Partial<SetupState>) => void;
  errors: Record<string, string>;
}

export const Step2Form: React.FC<Step2FormProps> = ({ state, onChange, errors }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getFieldError = (field: string) => {
    if (errors[field]) return errors[field];
    if (!touched[field]) return null;
    
    if (field === 'elecRate' && (!state.elecRate || state.elecRate <= 0)) return 'ค่าไฟต่อหน่วยต้องมากกว่า 0 บาท';
    if (field === 'waterRatePrice') {
      const w = parseFloat(state.waterRatePrice);
      if (isNaN(w) || w < 0 || String(state.waterRatePrice).trim() === '') return 'กรุณาระบุมูลค่าค่าน้ำ หรือระบุเป็น 0';
    }
    if (field === 'promptPayNumber') {
      if (!state.promptPayNumber) return 'กรุณากรอกเบอร์โทรหรือเลขผู้รับโอนเงิน';
      if (state.promptPayNumber.replace(/\D/g, '').length < 10) return 'เบอร์โทรหรือเลขผู้รับโอนเงินต้องมีอย่างน้อย 10 หลัก';
    }
    if (field === 'accountName') {
      if (!state.accountName.trim()) return 'กรุณาระบุชื่อบัญชีผู้รับโอน';
      if (!/^[ก-๙\s.\-()]+$/.test(state.accountName.trim())) return 'กรุณาระบุชื่อบัญชีเป็นภาษาไทยเท่านั้น';
    }
    if (field === 'meterRolloverLimit' && state.meterRolloverLimit <= 0) return 'มิเตอร์ต้องมากกว่า 0';
    if (field === 'agreeTerms' && !state.agreeTerms) return 'กรุณายอมรับเงื่อนไขการใช้บริการ';
    return null;
  };

  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  // File Upload handler
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('กรุณาเลือกไฟล์รูปภาพที่ขนาดไม่เกิน 5 MB');
        return;
      }
      setIsUploading(true);
        setTimeout(() => {
          if (state.logoUrl && state.logoUrl.startsWith('blob:')) {
            URL.revokeObjectURL(state.logoUrl);
          }
          const previewUrl = URL.createObjectURL(file);
          onChange({ logoUrl: previewUrl, logoFile: file, logoScale: 1.0, logoPositionX: 0, logoPositionY: 0 });
          setIsUploading(false);
        }, 300);
    }
  };

  const handleRemoveLogo = () => {
    if (state.logoUrl && state.logoUrl.startsWith('blob:')) {
      URL.revokeObjectURL(state.logoUrl);
    }
    onChange({ logoUrl: '', logoFile: null, logoScale: 1.0, logoPositionX: 0, logoPositionY: 0 });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });

  const handlePointerDown = (e: React.PointerEvent) => {
    if (!state.logoUrl) return;
    e.preventDefault();
    isDragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging.current || !state.logoUrl) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    dragStart.current = { x: e.clientX, y: e.clientY };
    
    // Normalize based on 64px viewport
    const normalizedDx = dx / 64;
    const normalizedDy = dy / 64;
    
    const newX = (state.logoPositionX || 0) + normalizedDx;
    const newY = (state.logoPositionY || 0) + normalizedDy;
    
    // Clamp to prevent disappearing
    onChange({
      logoPositionX: Math.max(-2.0, Math.min(2.0, newX)),
      logoPositionY: Math.max(-2.0, Math.min(2.0, newY))
    });
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (isDragging.current) {
      isDragging.current = false;
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  // Zoom logo controls
  const handleZoomIn = () => {
    onChange({ logoScale: Math.min(3.0, (state.logoScale || 1.0) + 0.1) });
  };

  const handleZoomOut = () => {
    onChange({ logoScale: Math.max(0.5, (state.logoScale || 1.0) - 0.1) });
  };

  const handleZoomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ logoScale: parseFloat(e.target.value) || 1.0 });
  };

  const handleResetZoom = () => {
    onChange({ logoScale: 1.0, logoPositionX: 0, logoPositionY: 0 });
  };

  // Electricity button handlers
  const handleElecIncrement = () => {
    onChange({ elecRate: (state.elecRate || 0) + 1 });
  };

  const handleElecDecrement = () => {
    onChange({ elecRate: Math.max(1, (state.elecRate || 0) - 1) });
  };

  // Water button handlers
  const handleWaterIncrement = () => {
    const current = parseFloat(state.waterRatePrice as any);
    const val = isNaN(current) ? 0 : current;
    onChange({ waterRatePrice: String(val + 1) });
  };

  const handleWaterDecrement = () => {
    const current = parseFloat(state.waterRatePrice as any);
    const val = isNaN(current) ? 0 : current;
    onChange({ waterRatePrice: String(Math.max(0, val - 1)) });
  };

  // Helper for PromptPay Formatting in real-time
  const handlePromptPayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value.replace(/\D/g, ''); // retain only digits
    if (raw.length > 13) {
      raw = raw.slice(0, 13);
    }
    onChange({ promptPayNumber: raw });
  };

  const getFormattedPromptPay = (raw: string) => {
    if (!raw) return '';
    if (raw.length === 10) {
      return `${raw.slice(0, 3)}-${raw.slice(3, 6)}-${raw.slice(6)}`;
    } else if (raw.length === 13) {
      return `${raw.slice(0, 1)}-${raw.slice(1, 5)}-${raw.slice(5, 10)}-${raw.slice(10, 12)}-${raw.slice(12)}`;
    }
    return raw;
  };

  return (
    <div className="space-y-6">
      
      {/* Title block */}
      <div className="border-b border-slate-100 pb-4">
        <h2 className="text-xl font-sans font-bold text-slate-800 flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center font-bold text-sm border border-amber-100 shrink-0 select-none">
            2
          </span>
          <span>ตั้งค่าบิลหอพัก</span>
        </h2>
        <p className="text-sm font-sans text-slate-500 mt-1 pl-10 leading-normal">
          เลือกธีมสี อัปโหลดตราสัญญาลักษณ์หรือโลโก้หอพักของท่าน และกำหนดอัตราสิ้นเปลืองบิลพื้นฐาน
        </p>
      </div>

      {/* 1. Theme Selection Area */}
      <div className="font-sans">
        <label className="block text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
          <Palette className="w-4 h-4 text-indigo-600" />
          เลือกสีธีมบิลหอพักหลักของคุณ <span className="text-rose-500">*</span>
        </label>
        
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5">
          {themePresets.map((preset) => {
            const isSelected = state.themeId === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => onChange({ themeId: preset.id })}
                className={`p-3 rounded-2xl border text-center transition cursor-pointer flex flex-col items-center justify-between gap-2 grow ${
                  isSelected 
                    ? 'border-indigo-600 bg-white ring-4 ring-indigo-50 shadow-md' 
                    : 'border-slate-150 hover:border-slate-300 bg-slate-50/40'
                }`}
              >
                {/* Color Dot indicator */}
                <div 
                  className="w-7 h-7 rounded-full shadow-sm relative flex items-center justify-center border border-black/10"
                  style={{ backgroundColor: preset.hex }}
                >
                  {isSelected && (
                    <span className="w-3.5 h-3.5 rounded-full bg-white flex items-center justify-center text-slate-800 scale-90">
                      <CheckCircle2 className="w-3 h-3 text-slate-800" strokeWidth={3} />
                    </span>
                  )}
                </div>
                
                {/* Name */}
                <span className={`text-[11px] font-bold leading-tight ${isSelected ? 'text-indigo-950 font-extrabold' : 'text-slate-500'}`}>
                  {(() => {
                    const parts = preset.name.split(' (');
                    if (parts.length > 1) {
                      return (
                        <>
                          <span className="block">{parts[0]}</span>
                          <span className="block text-[9.5px] text-slate-400 font-normal mt-0.5 whitespace-nowrap">
                            ({parts[1].replace(')', '').toLowerCase()})
                          </span>
                        </>
                      );
                    }
                    return <span>{preset.name}</span>;
                  })()}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. Logo Uploader */}
      <div className="font-sans border-t border-slate-100 pt-5">
        <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
          <Upload className="w-4 h-4 text-indigo-600" />
          โลโก้หอพักของคุณ (แนะนำรูปทรงวงกลมหรือสี่เหลี่ยมจัตุรัส)
        </label>
        <p className="text-xs text-slate-400 mb-3 ml-5">
          ระบบจะประทับหัวท้ายของบิลเพื่อแสดงถึงความน่าเชื่อถือ
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* File Picker Container */}
          <div className="border-2 border-dashed border-slate-200 rounded-2xl p-4 flex flex-col items-center justify-center bg-slate-50/50 hover:bg-slate-50 hover:border-indigo-300 transition duration-150 relative">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleLogoUpload}
              accept="image/png, image/jpeg, image/webp"
              className="hidden"
              disabled={isUploading}
            />
            
            {isUploading ? (
              <div className="text-center py-4 w-full h-full flex flex-col items-center justify-center">
                <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-3"></div>
                <span className="text-xs font-bold text-slate-700">กำลังอัปโหลด...</span>
              </div>
            ) : state.logoUrl ? (
              <div className="flex flex-col items-center">
                {/* Image preview box */}
                <div 
                  className="w-16 h-16 rounded-full border-2 overflow-hidden shadow mb-3 flex items-center justify-center bg-white relative"
                  style={{ 
                    borderColor: themePresets.find(t => t.id === state.themeId)?.hex || '#10b981',
                    cursor: state.logoUrl ? 'grab' : 'default',
                    touchAction: 'none',
                    clipPath: 'circle(50% at 50% 50%)',
                    WebkitMaskImage: 'radial-gradient(circle, #000 99%, transparent 100%)',
                    maskImage: 'radial-gradient(circle, #000 99%, transparent 100%)',
                    isolation: 'isolate'
                  }}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerCancel={handlePointerUp}
                  onPointerLeave={handlePointerUp}
                >
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      clipPath: 'circle(50% at 50% 50%)',
                      transformOrigin: 'center center',
                      transform: `translate(${(state.logoPositionX || 0) * 100}%, ${(state.logoPositionY || 0) * 100}%) scale(${state.logoScale || 1.0})`
                    }}
                  >
                    <img
                      src={state.logoUrl}
                      alt="Logo Upload"
                      className="pointer-events-none"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center center',
                        display: 'block'
                      }}
                      referrerPolicy="no-referrer"
                    />
                  </div>
                </div>
                <span className="text-xs text-slate-500 font-semibold mb-2">อัปโหลดภาพเรียบร้อย!</span>
                <button
                  type="button"
                  onClick={handleRemoveLogo}
                  className="text-rose-500 text-xs font-bold hover:underline inline-flex items-center gap-1 cursor-pointer"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>ลบโลโก้</span>
                </button>
              </div>
            ) : (
              <div 
                className="text-center py-4 cursor-pointer w-full h-full flex flex-col items-center"
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="w-10 h-10 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center mb-2">
                  <Upload className="w-5 h-5" />
                </div>
                <span className="text-xs font-bold text-slate-700">คลิกเพื่ออัปโหลดโลโก้</span>
                <p className="text-[10px] text-slate-400 mt-1">ไฟล์ PNG, JPG หรือ WEBP (ขนาดไม่เกิน 5 MB)</p>
              </div>
            )}
          </div>

          {/* Logo Size/Position Adjustments */}
          <div className={`p-4 rounded-2xl border ${state.logoUrl ? 'border-indigo-100 bg-indigo-50/20' : 'border-slate-100 bg-slate-50/30 opacity-60'} flex flex-col justify-center`}>
            <span className="text-xs font-bold text-slate-600 mb-2 flex items-center gap-1">
              <Settings className="w-3.5 h-3.5" />
              เครื่องมือปรับขนาดโลโก้ (Zoom & Pan Control)
            </span>
            <span className="text-[10px] text-slate-500 mb-2 block">
              💡 คุณสามารถคลิกค้างที่โลโก้และลากเพื่อเลื่อนตำแหน่งภาพได้
            </span>
            
            {/* Slider and Buttons */}
            <div className="flex items-center gap-2 mt-2">
              <button
                type="button"
                disabled={!state.logoUrl}
                onClick={handleZoomOut}
                className="p-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-800 disabled:opacity-50 cursor-pointer"
                title="ย่อขนาด"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              
              <input
                type="range"
                disabled={!state.logoUrl}
                min="0.5"
                max="3.0"
                step="0.1"
                value={state.logoScale}
                onChange={handleZoomChange}
                className="flex-1 accent-indigo-600 h-1 bg-slate-200 rounded-lg cursor-pointer disabled:opacity-50"
              />

              <button
                type="button"
                disabled={!state.logoUrl}
                onClick={handleZoomIn}
                className="p-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-800 disabled:opacity-50 cursor-pointer"
                title="ขยายขนาด"
              >
                <ZoomIn className="w-4 h-4" />
              </button>

              <button
                type="button"
                disabled={!state.logoUrl}
                onClick={handleResetZoom}
                className="p-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-800 disabled:opacity-50 cursor-pointer"
                title="รีเซ็ต"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>
            {state.logoUrl && (
              <span className="text-[10px] text-slate-400 mt-2 text-right">
                เครื่องเพิ่มขยายปัจจุบัน: <b className="font-mono">{state.logoScale?.toFixed(1)}x</b>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* 3. Utilities Billing Settings */}
      <div className="font-sans border-t border-slate-100 pt-5 space-y-5">
        <h4 className="font-bold text-slate-800 text-sm md:text-base flex items-center gap-1.5 mb-2">
          ⚙️ ตั้งค่าใบแจ้งหนี้ ค่าน้ำ/ค่าไฟ เริ่มต้นของหอพัก
        </h4>

        {/* Electricity Rate and Water Rate */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          
          {/* Elec Rate */}
          <div className="border border-slate-100 rounded-2xl p-4 bg-white shadow-sm flex flex-col justify-between">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
                <Zap className="w-4 h-4 text-amber-500 fill-amber-400" />
                ค่าไฟฟ้า <span className="text-rose-500">*</span>
              </label>
              <p className="text-[11px] text-slate-400 mb-3.5">
                อัตราเริ่มต้นต่อหน่วยหรือเหมาจ่ายที่เจ้าของเรียกเก็บ
              </p>
            </div>

            {/* Toggle format */}
            <div className="grid grid-cols-2 gap-2 bg-slate-50 p-1.5 rounded-xl border border-slate-200/50 mb-3.5">
              <button
                type="button"
                onClick={() => onChange({ elecRateType: 'per_unit' })}
                className={`py-1.5 text-xs font-semibold rounded-lg text-center cursor-pointer ${
                  (!state.elecRateType || state.elecRateType === 'per_unit')
                    ? 'bg-white text-indigo-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                ตามมิเตอร์ บาท/หน่วย
              </button>
              <button
                type="button"
                onClick={() => onChange({ elecRateType: 'per_room' })}
                className={`py-1.5 text-xs font-semibold rounded-lg text-center cursor-pointer ${
                  state.elecRateType === 'per_room'
                    ? 'bg-white text-indigo-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                เหมาจ่าย บาท/ห้อง
              </button>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleElecDecrement}
                className="w-10 h-10 rounded-xl bg-slate-150 hover:bg-slate-200 text-slate-700 flex items-center justify-center font-black text-sm select-none transition cursor-pointer"
              >
                <Minus className="w-4 h-4" />
              </button>
              
              <div className="relative flex-1 rounded-xl">
                <input
                  type="number"
                  min="1"
                  value={state.elecRate || ''}
                  onChange={(e) => onChange({ elecRate: Math.max(1, parseInt(e.target.value) || 0) })}
                  onBlur={() => handleBlur('elecRate')}
                  className={`w-full border rounded-xl px-3 py-2 text-center text-sm font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-100 ${getFieldError('elecRate') ? 'border-rose-300' : 'border-slate-200'}`}
                />
                <span className="absolute right-3.5 top-2.5 text-xs text-slate-400 font-semibold pointer-events-none">{(!state.elecRateType || state.elecRateType === 'per_unit') ? 'บาท/หน่วย' : 'บาท/ห้อง'}</span>
              </div>

              <button
                type="button"
                onClick={handleElecIncrement}
                className="w-10 h-10 rounded-xl bg-slate-150 hover:bg-slate-200 text-slate-700 flex items-center justify-center font-black text-sm select-none transition cursor-pointer"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {getFieldError('elecRate') && (
              <p className="text-xs text-rose-500 mt-2 flex items-center gap-1">
                {getFieldError('elecRate')}
              </p>
            )}
          </div>

          {/* Water Rate */}
          <div className="border border-slate-100 rounded-2xl p-4 bg-white shadow-sm space-y-3.5">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
                <Droplet className="w-4 h-4 text-sky-500 fill-sky-400" />
                ค่าน้ำประปา <span className="text-rose-500">*</span>
              </label>
              <p className="text-[11px] text-slate-400">
                ราคาค่าบริการน้ำประปาต่อผู้เข้าเช่าห้องหอ
              </p>
            </div>

            {/* Toggle format */}
            <div className="grid grid-cols-3 gap-2 bg-slate-50 p-1.5 rounded-xl border border-slate-200/50">
              <button
                type="button"
                onClick={() => onChange({ waterRateType: 'per_room' })}
                className={`py-1 text-xs font-semibold rounded-lg text-center cursor-pointer leading-tight ${
                  state.waterRateType === 'per_room'
                    ? 'bg-white text-indigo-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                เหมาจ่าย<br/>บาท/ห้อง
              </button>
              <button
                type="button"
                onClick={() => onChange({ waterRateType: 'per_person' })}
                className={`py-1 text-xs font-semibold rounded-lg text-center cursor-pointer leading-tight ${
                  state.waterRateType === 'per_person'
                    ? 'bg-white text-indigo-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                เหมาจ่าย<br/>บาท/คน
              </button>
              <button
                type="button"
                onClick={() => onChange({ waterRateType: 'per_unit' })}
                className={`py-1 text-xs font-semibold rounded-lg text-center cursor-pointer leading-tight ${
                  state.waterRateType === 'per_unit'
                    ? 'bg-white text-indigo-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                ตามมิเตอร์<br/>บาท/หน่วย
              </button>
            </div>

            {/* Input Price */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleWaterDecrement}
                className="w-10 h-10 rounded-xl bg-slate-150 hover:bg-slate-200 text-slate-700 flex items-center justify-center font-black text-sm select-none transition cursor-pointer"
              >
                <Minus className="w-4 h-4" />
              </button>
              
              <div className="relative flex-1 rounded-xl">
                <input
                  type="number"
                  min="0"
                  value={state.waterRatePrice}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    if (!isNaN(val) && val < 0) return;
                    onChange({ waterRatePrice: e.target.value });
                  }}
                  onBlur={() => handleBlur('waterRatePrice')}
                  placeholder="เช่น 150"
                  className={`w-full border rounded-xl px-3 py-2 text-center text-sm font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-100 placeholder:font-normal ${getFieldError('waterRatePrice') ? 'border-rose-300' : 'border-slate-200'}`}
                />
                <span className="absolute right-3.5 top-2.5 text-xs text-slate-400 font-semibold pointer-events-none">{state.waterRateType === 'per_room' ? 'บาท/ห้อง' : state.waterRateType === 'per_person' ? 'บาท/คน' : 'บาท/หน่วย'}</span>
              </div>

              <button
                type="button"
                onClick={handleWaterIncrement}
                className="w-10 h-10 rounded-xl bg-slate-150 hover:bg-slate-200 text-slate-700 flex items-center justify-center font-black text-sm select-none transition cursor-pointer"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {getFieldError('waterRatePrice') && (
              <p className="text-xs text-rose-500 mt-2 flex items-center gap-1">
                {getFieldError('waterRatePrice')}
              </p>
            )}

          </div>
        </div>

        {/* 4. Common Fee & wifi togglers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Common Fee Toggle */}
          <div className="border border-slate-100 rounded-2xl p-4 bg-white hover:shadow-sm transition">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-slate-700">มีค่าบริการส่วนกลาง</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={state.hasCommonFee}
                  onChange={(e) => onChange({ hasCommonFee: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
              </label>
            </div>

            {state.hasCommonFee && (
              <div className="mt-3 space-y-3 pt-3 border-t border-slate-100">
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="0"
                      placeholder="ราคาบาท"
                      value={state.commonFeePrice}
                      onChange={(e) => onChange({ commonFeePrice: e.target.value })}
                      className={`w-1/2 border rounded-xl px-3 py-1.5 text-xs font-bold text-slate-800 focus:ring-1 focus:ring-indigo-500 focus:outline-none ${errors.commonFeePrice ? 'border-rose-300' : 'border-slate-200'}`}
                    />
                    <select
                      value={state.commonFeeType}
                      onChange={(e) => onChange({ commonFeeType: e.target.value as FeeType })}
                      className="w-1/2 border border-slate-200 rounded-xl px-2 py-1.5 text-xs text-slate-700 font-medium focus:ring-1 focus:ring-indigo-500 focus:outline-none bg-white"
                    >
                      <option value="per_room">บาท/ห้อง</option>
                      <option value="per_person">บาท/คน</option>
                    </select>
                  </div>
                  {errors.commonFeePrice && (
                    <p className="text-xs text-rose-500 mt-1 flex items-center gap-1">
                      {errors.commonFeePrice}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Wi-Fi Toggle */}
          <div className="border border-slate-100 rounded-2xl p-4 bg-white hover:shadow-sm transition">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-slate-700">มีค่าบริการอินเทอร์เน็ต Wi-Fi</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={state.hasWifiFee}
                  onChange={(e) => onChange({ hasWifiFee: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
              </label>
            </div>

            {state.hasWifiFee && (
              <div className="mt-3 space-y-3 pt-3 border-t border-slate-100">
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="0"
                      placeholder="ราคาบาท"
                      value={state.wifiFeePrice}
                      onChange={(e) => onChange({ wifiFeePrice: e.target.value })}
                      className={`w-1/2 border rounded-xl px-3 py-1.5 text-xs font-bold text-slate-800 focus:ring-1 focus:ring-indigo-500 focus:outline-none ${errors.wifiFeePrice ? 'border-rose-300' : 'border-slate-200'}`}
                    />
                    <select
                      value={state.wifiFeeType}
                      onChange={(e) => onChange({ wifiFeeType: e.target.value as FeeType })}
                      className="w-1/2 border border-slate-200 rounded-xl px-2 py-1.5 text-xs text-slate-700 font-medium focus:ring-1 focus:ring-indigo-500 focus:outline-none bg-white"
                    >
                      <option value="per_room">บาท/ห้อง</option>
                      <option value="per_person">บาท/คน</option>
                    </select>
                  </div>
                  {errors.wifiFeePrice && (
                    <p className="text-xs text-rose-500 mt-1 flex items-center gap-1">
                      {errors.wifiFeePrice}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 5. Advanced rollover configuration (Meter Loop) */}
        <div className="border border-slate-150 rounded-2xl bg-slate-50 p-2.5">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="w-full flex items-center justify-between text-xs font-semibold text-slate-600 px-2 py-1.5 cursor-pointer"
          >
            <span className="flex items-center gap-1.5">
              <Settings className="w-3.5 h-3.5 text-slate-400" />
              ตั้งค่าขั้นสูง (Advanced Settings)
            </span>
            {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {showAdvanced && (
            <div className="mt-3 p-3 bg-white border border-slate-200 rounded-xl space-y-3 animate-fade-in">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1">
                  เกณฑ์มิเตอร์ไฟฟ้าวนรอบการระบุหน่วย <span className="text-rose-500">*</span>
                </label>
                <div className="relative rounded-xl max-w-xs">
                  <input
                    type="number"
                    min="1"
                    value={state.meterRolloverLimit}
                    onChange={(e) => onChange({ meterRolloverLimit: Math.max(1, parseInt(e.target.value) || 0) })}
                    onBlur={() => handleBlur('meterRolloverLimit')}
                    className={`w-full border rounded-xl px-3 py-1.5 text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-100 ${getFieldError('meterRolloverLimit') ? 'border-rose-300' : 'border-slate-200'}`}
                  />
                  <span className="absolute right-3 top-2 text-[10px] text-slate-400 font-bold">หน่วย</span>
                </div>
                <p className="text-[10px] text-slate-400 leading-normal mt-1.5">
                  💡 คำอธิบาย: ใช้กรณีมิเตอร์ไฟฟ้าวนรอบกลับ เช่น จาก 9999 กลับเป็น 0001 (อัตโนมัติหักทอนหน่วยจริง)
                </p>
                {getFieldError('meterRolloverLimit') && (
                  <p className="text-[11px] text-rose-500 mt-1">
                    {getFieldError('meterRolloverLimit')}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

      </div>

      {/* 6. PromptPay Account Checking Details */}
      <div className="font-sans border-t border-slate-100 pt-5 space-y-4">
        <h4 className="font-bold text-slate-800 text-sm md:text-base flex items-center gap-1.5">
          <CreditCard className="w-4.5 h-4.5 text-indigo-600" />
          การทดสอบระบบตรวจสลิป และรับเงิน (PromptPay)
        </h4>
        <p className="text-xs text-slate-400 -mt-2">
          ใช้ในการสร้างเป้าหมายคิวอาร์แบบโอนเข้าบัญชีโดยตรงเพื่อตรวจจับสลิปอัตโนมัติ
        </p>
        <div className="bg-amber-50/80 border border-amber-100 rounded-xl px-3 py-2 flex items-start gap-1.5 mt-2 max-w-2xl">
          <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-[11px] font-sans text-amber-800 leading-relaxed font-medium">
            เพื่อความปลอดภัยของข้อมูลส่วนบุคคล (Privacy Safety) ระบบจะไม่บันทึกข้อมูลเบอร์ PromptPay และชื่อบัญชีลงในแบบร่างของเบราว์เซอร์อัตโนมัติ กรุณากรอกข้อมูลส่วนนี้อีกครั้งหากคุณมีการรีเฟรชหน้าเว็บ
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* PromptPay Number */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1">
              เบอร์ PromptPay ในการเงินตรวจสลิป <span className="text-rose-500">*</span>
            </label>
            <div className="relative rounded-2xl shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                <CreditCard className="w-4 h-4" />
              </div>
              <input
                type="text"
                id="input-promptpay"
                value={state.promptPayNumber}
                onChange={handlePromptPayChange}
                onBlur={() => handleBlur('promptPayNumber')}
                placeholder="เช่น 0935098808 หรือ เลขบัตรประชาชน"
                className={`block w-full pl-10 pr-3.5 py-3 text-sm rounded-2xl border ${
                  getFieldError('promptPayNumber') ? 'border-rose-300 focus:ring-rose-200' : 'border-slate-200 focus:ring-indigo-100'
                } focus:outline-none focus:ring-4 transition duration-150 text-slate-800 font-mono font-bold bg-slate-50/50 focus:bg-white`}
              />
            </div>
            {state.promptPayNumber && (
              <p className="text-[10px] text-indigo-600 mt-1">
                จัดรูปแบบเบอร์โทร: <b className="font-mono">{getFormattedPromptPay(state.promptPayNumber)}</b>
              </p>
            )}
            {getFieldError('promptPayNumber') && (
              <p className="text-xs text-rose-500 mt-1 flex items-center gap-1">
                {getFieldError('promptPayNumber')}
              </p>
            )}
          </div>

          {/* Account Recipient Name */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1">
              ชื่อบัญชีผู้รับเงินสะกดตามจริง <span className="text-rose-500">*</span>
            </label>
            <div className="relative rounded-2xl shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                <User className="w-4 h-4" />
              </div>
              <input
                type="text"
                id="input-account-name"
                value={state.accountName}
                onChange={(e) => onChange({ accountName: e.target.value })}
                onBlur={() => handleBlur('accountName')}
                placeholder="เช่น นาย ภูวนาท ทานาลาด"
                className={`block w-full pl-10 pr-3.5 py-3 text-sm rounded-2xl border ${
                  getFieldError('accountName') ? 'border-rose-300 focus:ring-rose-200' : 'border-slate-200 focus:ring-indigo-100'
                } focus:outline-none focus:ring-4 transition duration-150 text-slate-800 font-medium bg-slate-50/50 focus:bg-white`}
              />
            </div>
            <p className="text-[10px] text-amber-700 font-bold leading-relaxed pt-1.5 select-none pl-1">
              💡 ลองสแกน QR Code เพื่อตรวจสอบชื่อก่อนได้ เพื่อความแม่นยำ
            </p>
            {getFieldError('accountName') && (
              <p className="text-xs text-rose-500 mt-1 flex items-center gap-1">
                {getFieldError('accountName')}
              </p>
            )}
          </div>
        </div>

        {/* Promo code & agree checkboxes */}
        <div className="space-y-4 pt-3">
          {/* Promo Code fields */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5 flex items-center gap-1">
              <Percent className="w-4 h-4 text-emerald-500" />
              รหัสแนะนำ / รหัสโปรโมชั่น (ถ้ามี)
            </label>
            <input
              type="text"
              value={state.promoCode}
              onChange={(e) => onChange({ promoCode: e.target.value })}
              placeholder="ใส่โปรโมชั่นโค้ดที่นี่เพื่อรับรับส่วนลดพิเศษ"
              className="block w-full max-w-md px-3.5 py-2.5 text-xs rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-200 text-slate-700"
            />
          </div>

          {/* Agreement checkboxes with absolute security label */}
          <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100 space-y-3">
            
            <label className="flex items-start gap-2.5 cursor-pointer group">
              <input
                type="checkbox"
                data-testid="agree-terms-checkbox"
                checked={state.agreeTerms}
                onChange={(e) => {
                  onChange({ agreeTerms: e.target.checked });
                  handleBlur('agreeTerms');
                }}
                className="mt-1 w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500 cursor-pointer"
              />
              <span className="text-xs text-slate-600 leading-normal select-none group-hover:text-slate-950 transition">
                ฉันได้อ่านและยอมรับ <span className="text-indigo-600 font-semibold underline">นโยบายคุ้มครองข้อมูลส่วนบุคคล (PDPA)</span> และ <span className="text-indigo-600 font-semibold underline">เงื่อนไขการใช้บริการ</span> สำหรับแพลตฟอร์มจัดการหอพักอย่างเป็นทางการ และรับทราบว่าจะมีการนำข้อมูลชื่อบัญชีและ PromptPay ไปสร้างบิลออนไลน์ <span className="text-rose-500">*</span>
              </span>
            </label>

            {getFieldError('agreeTerms') && (
              <p className="text-xs text-rose-500 mt-1 flex items-center gap-1">
                {getFieldError('agreeTerms')}
              </p>
            )}

            <div className="flex items-center gap-2 text-[10px] text-slate-400 font-sans">
              <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span>ความปลอดภัยสูงสุดสำหรับการส่งข้อมูลด้วยโปรโตคอล TLS แบบเข้ารหัส (frontend dummy)</span>
            </div>
          </div>
          
        </div>

      </div>

    </div>
  );
};
